import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Save, Loader2, User, Phone, Mail, Calendar, Heart,
  Scissors, FileText, Camera, Star, ClipboardList, PlusCircle,
  Send, Trash2, ChevronDown, ChevronUp, Upload
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section wrapper
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="glass-card border-white/10">
      <CardHeader className="cursor-pointer py-4 px-5" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
              <Icon size={16} className="text-cyan-400" />
            </div>
            <CardTitle className="text-white text-base">{title}</CardTitle>
          </div>
          {open ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
        </div>
      </CardHeader>
      {open && <CardContent className="pt-0 px-5 pb-5">{children}</CardContent>}
    </Card>
  );
};

const FieldRow = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-white/50 text-xs uppercase tracking-wider">{label}</label>
    {children}
  </div>
);

const ClientProfile = ({ memberId, token, onBack }) => {
  const [member, setMember] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [healthForm, setHealthForm] = useState(null);
  const [treatmentNotes, setTreatmentNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [intakeEmail, setIntakeEmail] = useState("");

  // Editable fields
  const [form, setForm] = useState({
    name: "", email: "", phone: "", birthday: "", status: "",
    notes: "",
    skin_type: "", fitzpatrick_type: "", main_concerns: "",
    sensitivity_level: "", allergies: "",
    skin_goals: "", recommended_services: "", next_appointment_date: "",
    lifestyle_notes: "", preferences: "",
    products_recommended: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/members/${memberId}?token=${token}`);
      const { member: m, transactions: t, health_form: h, treatment_notes: tn } = res.data;
      setMember(m);
      setTransactions(t || []);
      setHealthForm(h);
      setTreatmentNotes(tn || []);
      setIntakeEmail(m.email);

      // Populate form
      const sp = m.skin_profile || {};
      const tp = m.treatment_plan || {};
      const pn = m.personal_notes || {};
      setForm({
        name: m.name || "", email: m.email || "", phone: m.phone || "",
        birthday: m.birthday || "", status: m.status || "", notes: m.notes || "",
        skin_type: sp.skin_type || "", fitzpatrick_type: sp.fitzpatrick_type || "",
        main_concerns: (sp.main_concerns || []).join(", "),
        sensitivity_level: sp.sensitivity_level || "", allergies: sp.allergies || "",
        skin_goals: tp.skin_goals || "", recommended_services: tp.recommended_services || "",
        next_appointment_date: tp.next_appointment_date || "",
        lifestyle_notes: pn.lifestyle_notes || "", preferences: pn.preferences || "",
        products_recommended: (m.products_recommended || []).join(", ")
      });
    } catch {
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  }, [memberId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone,
        birthday: form.birthday || null, status: form.status, notes: form.notes || null,
        skin_profile: {
          skin_type: form.skin_type || null,
          fitzpatrick_type: form.fitzpatrick_type || null,
          main_concerns: form.main_concerns ? form.main_concerns.split(",").map(s => s.trim()).filter(Boolean) : [],
          sensitivity_level: form.sensitivity_level || null,
          allergies: form.allergies || null
        },
        treatment_plan: {
          skin_goals: form.skin_goals || null,
          recommended_services: form.recommended_services || null,
          next_appointment_date: form.next_appointment_date || null
        },
        personal_notes: {
          lifestyle_notes: form.lifestyle_notes || null,
          preferences: form.preferences || null
        },
        products_recommended: form.products_recommended ? form.products_recommended.split(",").map(s => s.trim()).filter(Boolean) : []
      };
      await axios.put(`${API}/admin/members/${memberId}?token=${token}`, payload);
      toast.success("Client profile saved!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSendIntakeForm = async () => {
    try {
      await axios.post(`${API}/admin/send-intake-form?token=${token}`, {
        email: intakeEmail, name: form.name
      });
      toast.success(`Intake form sent to ${intakeEmail}`);
      setShowIntakeModal(false);
    } catch {
      toast.error("Failed to send intake form");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;
  }

  if (!member) {
    return <div className="text-white/60 text-center py-20">Client not found</div>;
  }

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button onClick={onBack} variant="ghost" className="text-white/60 hover:text-white" data-testid="back-to-members">
            <ArrowLeft size={18} className="mr-2" /> Back
          </Button>
          <div>
            <h2 className="font-italiana text-2xl text-white">{member.name}</h2>
            <p className="text-white/40 text-sm">{member.email} {member.last_visit_date ? `• Last visit: ${member.last_visit_date}` : ""}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowIntakeModal(true)} variant="outline" className="btn-secondary rounded-full" data-testid="send-intake-btn">
            <Send size={14} className="mr-2" /> Send Intake Form
          </Button>
          <Button onClick={handleSave} disabled={saving} className="btn-primary rounded-full" data-testid="save-profile-btn">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save Profile
          </Button>
        </div>
      </div>

      {/* 1. Client Information */}
      <Section title="Client Information" icon={User} defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FieldRow label="Full Name">
            <Input value={form.name} onChange={e => update("name", e.target.value)} className="input-dark rounded-lg" data-testid="profile-name" />
          </FieldRow>
          <FieldRow label="Phone">
            <Input value={form.phone} onChange={e => update("phone", e.target.value)} className="input-dark rounded-lg" data-testid="profile-phone" />
          </FieldRow>
          <FieldRow label="Email">
            <Input value={form.email} onChange={e => update("email", e.target.value)} className="input-dark rounded-lg" data-testid="profile-email" />
          </FieldRow>
          <FieldRow label="Birthday">
            <Input type="date" value={form.birthday} onChange={e => update("birthday", e.target.value)} className="input-dark rounded-lg" data-testid="profile-birthday" />
          </FieldRow>
          <FieldRow label="Status">
            <select value={form.status} onChange={e => update("status", e.target.value)} className="w-full input-dark rounded-lg px-3 py-2 text-sm" data-testid="profile-status">
              <option value="lead">Lead</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="vip">VIP</option>
            </select>
          </FieldRow>
          <FieldRow label="Last Visit">
            <Input value={member.last_visit_date || "No visits yet"} disabled className="input-dark rounded-lg opacity-60" />
          </FieldRow>
        </div>
      </Section>

      {/* 2. Skin Profile */}
      <Section title="Skin Profile" icon={Heart}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FieldRow label="Skin Type">
            <select value={form.skin_type} onChange={e => update("skin_type", e.target.value)} className="w-full input-dark rounded-lg px-3 py-2 text-sm" data-testid="profile-skin-type">
              <option value="">Select...</option>
              <option value="Normal">Normal</option>
              <option value="Oily">Oily</option>
              <option value="Dry">Dry</option>
              <option value="Combination">Combination</option>
              <option value="Sensitive">Sensitive</option>
            </select>
          </FieldRow>
          <FieldRow label="Fitzpatrick Type">
            <select value={form.fitzpatrick_type} onChange={e => update("fitzpatrick_type", e.target.value)} className="w-full input-dark rounded-lg px-3 py-2 text-sm" data-testid="profile-fitzpatrick">
              <option value="">Select...</option>
              <option value="Type I">Type I - Very Fair</option>
              <option value="Type II">Type II - Fair</option>
              <option value="Type III">Type III - Medium</option>
              <option value="Type IV">Type IV - Olive</option>
              <option value="Type V">Type V - Brown</option>
              <option value="Type VI">Type VI - Dark Brown/Black</option>
            </select>
          </FieldRow>
          <FieldRow label="Sensitivity Level">
            <select value={form.sensitivity_level} onChange={e => update("sensitivity_level", e.target.value)} className="w-full input-dark rounded-lg px-3 py-2 text-sm" data-testid="profile-sensitivity">
              <option value="">Select...</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </FieldRow>
          <FieldRow label="Main Concerns (comma separated)">
            <Input value={form.main_concerns} onChange={e => update("main_concerns", e.target.value)} className="input-dark rounded-lg" placeholder="acne, pigmentation, aging" data-testid="profile-concerns" />
          </FieldRow>
          <FieldRow label="Allergies">
            <Input value={form.allergies} onChange={e => update("allergies", e.target.value)} className="input-dark rounded-lg" placeholder="None" data-testid="profile-allergies" />
          </FieldRow>
        </div>
      </Section>

      {/* 3. Treatment History */}
      <Section title="Treatment History" icon={ClipboardList}>
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowTreatmentModal(true)} className="btn-primary rounded-full" data-testid="add-treatment-note-btn">
              <PlusCircle size={14} className="mr-2" /> Add Visit Note
            </Button>
          </div>
          {treatmentNotes.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-6">No treatment notes yet</p>
          ) : (
            treatmentNotes.map(note => (
              <motion.div key={note.id} className="glass-card rounded-xl p-4 border border-white/5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-cyan-400 text-xs">{note.date_of_service}</p>
                    <p className="text-white font-semibold text-sm">{note.service_performed}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-400/60 hover:text-red-400" onClick={async () => {
                    await axios.delete(`${API}/admin/treatment-notes/${note.id}?token=${token}`);
                    toast.success("Note deleted");
                    fetchData();
                  }}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {note.steps_performed && <div><span className="text-white/40">Steps:</span> <span className="text-white/70">{note.steps_performed}</span></div>}
                  {note.products_used && <div><span className="text-white/40">Products:</span> <span className="text-white/70">{note.products_used}</span></div>}
                  {note.skin_reaction && <div><span className="text-white/40">Reaction:</span> <span className="text-white/70">{note.skin_reaction}</span></div>}
                  {note.results_outcome && <div><span className="text-white/40">Results:</span> <span className="text-white/70">{note.results_outcome}</span></div>}
                  {note.progress_notes && <div className="md:col-span-2"><span className="text-white/40">Notes:</span> <span className="text-white/70">{note.progress_notes}</span></div>}
                  {note.products_recommended?.length > 0 && <div className="md:col-span-2"><span className="text-white/40">Recommended:</span> <span className="text-white/70">{note.products_recommended.join(", ")}</span></div>}
                </div>
                {(note.before_photos?.length > 0 || note.after_photos?.length > 0) && (
                  <div className="flex gap-3 mt-3">
                    {note.before_photos?.map((p, i) => <img key={`b${i}`} src={p} alt="Before" className="w-20 h-20 rounded-lg object-cover border border-white/10" />)}
                    {note.after_photos?.map((p, i) => <img key={`a${i}`} src={p} alt="After" className="w-20 h-20 rounded-lg object-cover border border-cyan-500/30" />)}
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </Section>

      {/* 4. Products */}
      <Section title="Products" icon={Star}>
        <FieldRow label="Homecare Products Recommended (comma separated)">
          <Textarea value={form.products_recommended} onChange={e => update("products_recommended", e.target.value)} className="input-dark rounded-lg" placeholder="SPF 50, Retinol serum, Vitamin C..." rows={2} data-testid="profile-products" />
        </FieldRow>
      </Section>

      {/* 5. Treatment Plan */}
      <Section title="Treatment Plan" icon={Calendar}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FieldRow label="Skin Goals">
            <Textarea value={form.skin_goals} onChange={e => update("skin_goals", e.target.value)} className="input-dark rounded-lg" rows={2} data-testid="profile-skin-goals" />
          </FieldRow>
          <FieldRow label="Recommended Services & Frequency">
            <Textarea value={form.recommended_services} onChange={e => update("recommended_services", e.target.value)} className="input-dark rounded-lg" rows={2} data-testid="profile-rec-services" />
          </FieldRow>
          <FieldRow label="Next Appointment">
            <Input type="date" value={form.next_appointment_date} onChange={e => update("next_appointment_date", e.target.value)} className="input-dark rounded-lg" data-testid="profile-next-appt" />
          </FieldRow>
        </div>
      </Section>

      {/* 6. Personal Notes */}
      <Section title="Personal Notes" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldRow label="Lifestyle Notes (stress, work, habits)">
            <Textarea value={form.lifestyle_notes} onChange={e => update("lifestyle_notes", e.target.value)} className="input-dark rounded-lg" rows={3} data-testid="profile-lifestyle" />
          </FieldRow>
          <FieldRow label="Preferences (quiet service, pressure, add-ons)">
            <Textarea value={form.preferences} onChange={e => update("preferences", e.target.value)} className="input-dark rounded-lg" rows={3} data-testid="profile-preferences" />
          </FieldRow>
        </div>
        <div className="mt-4">
          <FieldRow label="General Notes">
            <Textarea value={form.notes} onChange={e => update("notes", e.target.value)} className="input-dark rounded-lg" rows={3} data-testid="profile-notes" />
          </FieldRow>
        </div>
      </Section>

      {/* 7. Transactions */}
      {transactions.length > 0 && (
        <Section title={`Transaction History (${transactions.length})`} icon={Scissors}>
          <div className="space-y-2">
            {transactions.map(t => (
              <div key={t.id} className="flex justify-between items-center py-2 border-b border-white/5 text-sm">
                <div>
                  <p className="text-white">{t.service_name}</p>
                  <p className="text-white/40 text-xs">{t.created_at?.split("T")[0]}</p>
                </div>
                <div className="text-right">
                  <p className="text-cyan-400 font-semibold">${t.amount?.toFixed(2)}</p>
                  <p className={`text-xs ${t.payment_status === "paid" ? "text-green-400" : "text-yellow-400"}`}>{t.payment_status}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 8. Health Form */}
      {healthForm && (
        <Section title="Health Intake Form" icon={FileText}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div><span className="text-white/40">Skin Type:</span> <span className="text-white/70">{healthForm.skin_type}</span></div>
            <div><span className="text-white/40">Allergies:</span> <span className="text-white/70">{healthForm.allergies || "None"}</span></div>
            <div><span className="text-white/40">Under Doctor Care:</span> <span className="text-white/70">{healthForm.under_doctor_care ? "Yes" : "No"}</span></div>
            {healthForm.health_conditions?.length > 0 && <div className="col-span-2"><span className="text-white/40">Conditions:</span> <span className="text-white/70">{healthForm.health_conditions.join(", ")}</span></div>}
            {healthForm.skin_concerns?.length > 0 && <div className="col-span-2"><span className="text-white/40">Skin Concerns:</span> <span className="text-white/70">{healthForm.skin_concerns.join(", ")}</span></div>}
            {healthForm.medications?.length > 0 && <div className="col-span-2"><span className="text-white/40">Medications:</span> <span className="text-white/70">{healthForm.medications.join(", ")}</span></div>}
            <div><span className="text-white/40">Signature:</span> <span className="text-white/70">{healthForm.consent_signature}</span></div>
            <div><span className="text-white/40">Date:</span> <span className="text-white/70">{healthForm.consent_date}</span></div>
          </div>
        </Section>
      )}

      {/* Treatment Note Modal */}
      <TreatmentNoteModal
        isOpen={showTreatmentModal}
        onClose={() => setShowTreatmentModal(false)}
        memberId={memberId}
        token={token}
        onSuccess={fetchData}
      />

      {/* Send Intake Form Modal */}
      <Dialog open={showIntakeModal} onOpenChange={setShowIntakeModal}>
        <DialogContent className="glass-card border-white/20 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-italiana text-xl text-white">Send Intake Form</DialogTitle>
            <DialogDescription className="text-white/60">Email the health intake form to this client</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FieldRow label="Email">
              <Input value={intakeEmail} onChange={e => setIntakeEmail(e.target.value)} className="input-dark rounded-lg" data-testid="intake-email-input" />
            </FieldRow>
            <Button onClick={handleSendIntakeForm} className="w-full btn-primary rounded-full" data-testid="send-intake-confirm-btn">
              <Send size={14} className="mr-2" /> Send Form
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Treatment Note Modal
const TreatmentNoteModal = ({ isOpen, onClose, memberId, token, onSuccess }) => {
  const [form, setForm] = useState({
    date_of_service: new Date().toISOString().split("T")[0],
    service_performed: "",
    steps_performed: "",
    products_used: "",
    skin_reaction: "",
    results_outcome: "",
    progress_notes: "",
    products_recommended: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.service_performed) { toast.error("Service performed is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/treatment-notes?token=${token}`, {
        member_id: memberId,
        ...form,
        products_recommended: form.products_recommended ? form.products_recommended.split(",").map(s => s.trim()).filter(Boolean) : []
      });
      toast.success("Treatment note added!");
      onSuccess();
      onClose();
      setForm({
        date_of_service: new Date().toISOString().split("T")[0],
        service_performed: "", steps_performed: "", products_used: "",
        skin_reaction: "", results_outcome: "", progress_notes: "", products_recommended: ""
      });
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-white/20 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-italiana text-xl text-white">Add Treatment Note</DialogTitle>
          <DialogDescription className="text-white/60">Record details from this visit</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Date *">
              <Input type="date" value={form.date_of_service} onChange={e => update("date_of_service", e.target.value)} className="input-dark rounded-lg" data-testid="note-date" />
            </FieldRow>
            <FieldRow label="Service Performed *">
              <Input value={form.service_performed} onChange={e => update("service_performed", e.target.value)} className="input-dark rounded-lg" placeholder="Signature Glam Facial" data-testid="note-service" />
            </FieldRow>
          </div>
          <FieldRow label="Steps Performed">
            <Textarea value={form.steps_performed} onChange={e => update("steps_performed", e.target.value)} className="input-dark rounded-lg" rows={2} placeholder="Cleanse, exfoliation, extractions, mask..." data-testid="note-steps" />
          </FieldRow>
          <FieldRow label="Products Used">
            <Input value={form.products_used} onChange={e => update("products_used", e.target.value)} className="input-dark rounded-lg" placeholder="Dermalogica cleanser, Image enzyme peel" data-testid="note-products" />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Skin Reaction">
              <Input value={form.skin_reaction} onChange={e => update("skin_reaction", e.target.value)} className="input-dark rounded-lg" placeholder="Mild redness" data-testid="note-reaction" />
            </FieldRow>
            <FieldRow label="Results / Outcome">
              <Input value={form.results_outcome} onChange={e => update("results_outcome", e.target.value)} className="input-dark rounded-lg" placeholder="Pores visibly reduced" data-testid="note-results" />
            </FieldRow>
          </div>
          <FieldRow label="Progress Notes">
            <Textarea value={form.progress_notes} onChange={e => update("progress_notes", e.target.value)} className="input-dark rounded-lg" rows={2} placeholder="Overall progress..." data-testid="note-progress" />
          </FieldRow>
          <FieldRow label="Products Recommended (comma separated)">
            <Input value={form.products_recommended} onChange={e => update("products_recommended", e.target.value)} className="input-dark rounded-lg" placeholder="SPF 50, Retinol serum" data-testid="note-recommended" />
          </FieldRow>
          <Button onClick={handleSubmit} disabled={saving} className="w-full btn-primary rounded-full" data-testid="save-treatment-note-btn">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save Treatment Note
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientProfile;
